"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Scroll-in reveal with fail-safe visibility.
 *
 * Robustness contract (learned the hard way): content must be visible
 * whenever JS is NOT running — SSR, no-JS browsers, a failed hydration,
 * or a broken bundle. Therefore the CSS never hides anything by default;
 * this component arms the hidden state *from JS on mount* for below-fold
 * elements only, then reveals them via IntersectionObserver.
 *
 * - No JS running        → content renders fully visible.
 * - JS running           → below-fold content gets a subtle entrance.
 * - prefers-reduced-motion → nothing is ever hidden; no animation.
 */
export default function Reveal({
  children,
  className = "",
  as: Tag = "div",
}: {
  children: ReactNode;
  className?: string;
  as?: "div" | "section" | "li" | "article";
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduced || typeof IntersectionObserver === "undefined") return;

    let armed = false;
    const disarm = () => {
      if (armed) {
        armed = false;
        setArmed(false);
      }
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          disarm();
          io.disconnect();
          window.removeEventListener("scroll", onScroll, scrollOpts);
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 },
    );
    io.observe(el);

    // Fallback for elements scrolled PAST without an IO notification
    // (e.g. instant programmatic jumps): if it's above the viewport,
    // it was already "revealed" from the user's perspective.
    const scrollOpts: AddEventListenerOptions = { passive: true };
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0) {
        disarm();
        io.disconnect();
        window.removeEventListener("scroll", onScroll, scrollOpts);
      }
    };
    window.addEventListener("scroll", onScroll, scrollOpts);

    // Below the fold → arm the entrance. In/near view → stays visible.
    const rect = el.getBoundingClientRect();
    if (rect.top > window.innerHeight * 0.95) {
      armed = true;
      setArmed(true);
    }
    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll, scrollOpts);
    };
  }, []);

  return (
    <Tag
      ref={ref as never}
      className={`reveal ${armed ? "reveal-armed" : ""} ${className}`.trim()}
    >
      {children}
    </Tag>
  );
}